import dayjs from 'dayjs';
import { Form as VeeForm, Field, defineRule } from 'vee-validate';
import { email, required } from '@vee-validate/rules';
import { defineComponent } from 'vue';
import { isStreetNumberFormat } from '@/shared/helpers/is-street-number-format';
import { decodeHtml } from '@/shared/helpers/decode';
import i18n from '@/i18n';

export const postalZipRegex = new RegExp(/^\s*[0-9]{5}\s*$|^\s*[A-Z][0-9][A-Z] ?[0-9][A-Z][0-9]\s*$/, 'i');

export const methods = {
  isImageName(fileName: string | null | undefined) {
    return !!(fileName?.match(/.(jpg|jpeg|png|gif|jfif|webp)$/i));
  },

  isPDFName(fileName: string) {
    return fileName?.slice(-3) === 'pdf';
  },

  isEmail(text: string): boolean {
    // eslint-disable-next-line max-len, vue/max-len
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(text).toLowerCase());
  },

  isPhone(text: string): boolean {
    return [10, 11, 12, 13].includes(text.match(/\d/g)?.length || 0);
  },

  isURL(text: string): boolean {
    const pattern = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]{1,63}(\.[a-zA-Z0-9-]{2,})+([\/?#].*)?$/;
    const trimmed = text.trim();
    return trimmed.length > 0 && pattern.test(trimmed);
  },

  newPassword(password: string): boolean {
    if (password.length < 8) {
      return false;
    }

    const passwordTests = [
      /[a-z]/, // Lowercase
      /[A-Z]/, // Uppercase
      /.{20,}/, // 20+ Characters
      /[\W_]/, // Any symbol (unicode included)
      /[\d]/, // Any digit
    ];

    return passwordTests
      .map((reg) => reg.test(password))
      .filter((test) => test)
      .length >= 3;
  },
};

export const isPasswordValid = (password: string) => {
  const isValid = methods.newPassword(password);
  return {
    isValid,
    message: !isValid
      ? 'New passwords must have a minimum of 8 characters, a mix of lowercase and uppercase characters, and a symbol or number.'
      : '',
  };
};

defineRule('required', (value: any) => (required(value) ? true : i18n.t('validation.fieldRequired') as string));
defineRule('email', email);
defineRule('url', (value: string) => (!value?.trim() || methods.isURL(value) || 'Url is invalid'));

defineRule('fieldExists', (value: string, [fieldKeys]: string[]) => (
  fieldKeys.split('_').includes(value) || 'Conditional field no longer exists! Please select another.'
));

defineRule('minDate', (day: string, [minDate]: string[]) => {
  const d = dayjs(new Date(day));

  if (!d.isValid() || minDate === '' || minDate === '0000-00-00') return true;

  return d.isAfter(minDate, 'day') || 'End Date cannot be before than the Start Date.';
});

defineRule('maxDate', (day: string, [maxDate]: string[]) => {
  const d = dayjs(new Date(day));

  if (!d.isValid() || maxDate === '' || maxDate === '0000-00-00') return true;

  return d.isBefore(maxDate, 'day') || 'Start Date cannot be greater than the End Date.';
});

defineRule('isBetweenMinAndMax', (value: string[], [min, max]: string[]) => (
  (value.length >= Number(min) && value.length <= Number(max))
    || 'Number of selected values cannot go below minimum and above maximum'
));

defineRule('duplicateValues', (values: string[]|string) => {
  if (values === undefined || values === null || values.length <= 1 || values === '') return true;
  const inputValues = typeof values === 'string' ? values.split('\n') : values;
  const hasDuplicates = inputValues.some((value, index) => {
    const lowerCaseValue = value.toLowerCase();
    return index !== inputValues.findIndex((v) => v.toLowerCase() === lowerCaseValue);
  });
  return !hasDuplicates || 'Duplicate values are not valid.';
});

defineRule('incompletePrimaryAddress', (_value: string, [isAddressComplete]: any[]) => (
  isAddressComplete || 'Address must be complete'
));

/** Ring time per call group entry, in seconds, as the call tracking API accepts it: a whole number from 8 to 15. */
export const MIN_CALL_GROUP_DIAL_TIMEOUT = 8;
export const MAX_CALL_GROUP_DIAL_TIMEOUT = 15;
export const DEFAULT_CALL_GROUP_DIAL_TIMEOUT = 15;

defineRule('callGroupDialTimeout', (value: unknown) => {
  const seconds = Number(value);
  const isValid = Number.isInteger(seconds) && seconds >= MIN_CALL_GROUP_DIAL_TIMEOUT && seconds <= MAX_CALL_GROUP_DIAL_TIMEOUT;
  return isValid || i18n.t('ivr.details.dialTimeoutValidationMessage') as string;
});

defineRule('streetNumberFormat', (value: string) => isStreetNumberFormat(value) || 'Street # must be valid');

defineRule('isValidLatitude', (value: string) => (
  (/^([-+]?([1-8]?[0-9](\.\d+)?|90(\.0+)?))$/).test(value)
    || 'Latitude must be between -90 and 90, with optional decimals (ex. -89.5).'
));

defineRule('isValidLongitude', (value: string) => (
  (/^([-+]?((1[0-7][0-9]|[1-9]?[0-9])(\.\d+)?|180(\.0+)?))$/).test(value)
    || 'Longitude must be between -180 and 180, with optional decimals (ex. -179.5).'
));

export default defineComponent({
  components: {
    Field,
    VeeForm,
  },
  methods,
});
